import React, { Component } from 'react';
import { connect } from 'react-redux';
import { getTitleContentLinkAndParameters } from './redux/selectors';
import HomeRoot from './Contents/Home/HomeRoot';
import JsxHelper from './Helpers/JsxHelper';
import menuitems from './menuitems';
import Cglobal from './cglobal';
import { link_click } from './redux/actions';
import RoleService from './Services/roleService';
import AuthService from './Services/authService';
import urlHelper from './Helpers/urlHelper';
import FaIcon from './Elements/FaIcon';
import { iconsEnabled } from './Helpers/iconHelper';
import ErrorService from './Services/ErrorService';
import UserGuideTitle from './Elements/UserGuideTitle';

// True when the browser url is the Home page itself ('/' or '/home'), the only case in which Home
// should be what renders before load() has resolved the url into a menu entry.
function isHomePath() {
  const slug = window.location.pathname.split('/')[1].toLocaleLowerCase();
  return slug === '' || slug === 'home';
}

class Content extends Component {
  constructor(props) {
    super(props);
    this.state = {
      link: isHomePath() ? this.defaultTitleAndLink : this.placeholderTitleAndLink,
      hasError: false,
    };
    this.myRef = React.createRef();
    Cglobal.contentScrollToTheTop = this.scrollToTheTop.bind(this);
    window.onpopstate = this.clickOnBack.bind(this);
  }

  static getDerivedStateFromError(error) {
    ErrorService.LogUIError(error);
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    this.setState({
      hasError: true,
      error: error.message,
      info: info.componentStack,
    });
  }

  defaultTitleAndLink = {
    title: 'Home',
    link: <HomeRoot />,
    icon: 'fa fa-igloo',
    key: 'user_key',
    url: 'home',
    file: 'Contents/Home/Home.tsx',
  };

  // What renders on a deep link / F5 to any non-Home page, for the one render between mount and
  // load() dispatching the real page. Home used to serve as that placeholder for every url, which
  // mounted the whole dashboard for a frame — long enough for its panels to fire their
  // load-on-mount fetches (three JiraIssue calls per page load), whose responses and error toasts
  // then landed on the page the user actually asked for. Inert on purpose: the page header renders
  // outside this, and load() dispatches synchronously in componentDidMount, so nothing is visible
  // long enough to be worth a spinner.
  placeholderTitleAndLink = {
    title: '',
    link: null,
    icon: '',
    key: '',
    url: '',
    file: '',
  };

  clickOnBack(name, skipPushState) {
    window.location.reload(true);
    // this.load(window.location.pathname)
    // window.setTimeout(()=>this.load(window.location.pathname), 2000);
  }

  UserRoot = null;

  componentDidMount() {
    this.load(window.location.pathname);
  }

  componentDidUpdate(prevProps) {
    if (prevProps !== this.props) {
      this.setState({ hasError: false });
    }
  }

  load(path) {
    path = `/${path.split('/')[1].toLocaleLowerCase()}`;
    if (path === '/logout') {
      AuthService.getInstance().Logout();
      return;
    }
    if (path !== '/') {
      for (const menuitem of menuitems) {
        const menuitemUrl = `/${menuitem.url.toLocaleLowerCase()}`;
        if (menuitem.visible === false) continue;
        if (menuitem.submenu) {
          for (const submenu of menuitem.submenu) {
            if (
              `/${submenu.url.toLocaleLowerCase()}` === path &&
              submenu.key &&
              RoleService.CheckUserPermission('submenu', submenu.key)
            ) {
              // webpackExclude keeps the variable-import context from sweeping test files
              // (and vitest with them) into production chunks.
              const Comp = React.lazy(() =>
                import(/* webpackExclude: /(__tests__|\.test\.|\.spec\.)/ */ `./${submenu.file}`)
              );
              this.props.link_click(
                <React.Suspense fallback={<div>Loading...</div>}>
                  <Comp />
                </React.Suspense>,
                submenu.text,
                submenu.icon,
                menuitem,
                menuitem.submenu.indexOf(submenu)
              );
              return;
            }
          }
        }

        if (menuitemUrl === path && menuitem.key && RoleService.CheckUserPermission('menu', menuitem.key)) {
          const Comp = React.lazy(() =>
            import(/* webpackExclude: /(__tests__|\.test\.|\.spec\.)/ */ `./${menuitem.file}`)
          );

          this.props.link_click(
            // Same inert fallback as the submenu branch above. It used to be <HomeRoot />, which
            // mounted the Home dashboard — and fired its panels' load-on-mount fetches — every
            // time a top-level page's lazy chunk was still loading.
            <React.Suspense fallback={<div>Loading...</div>}>
              <Comp />
            </React.Suspense>,
            menuitem.text,
            menuitem.icon,
            menuitem
          );
          return;
        }
      }
    }
  }

  scrollToTheTop() {
    this.myRef.current.scrollIntoView({ behavior: 'smooth' });
  }

  renderError() {
    return (
      <div
        className='Basecontent'
        key='kerr1'
      >
        <div className='pageheader bravo-ds'>
          <div className='page-head'>
            <div className='page-title'>
              <span className='eq-mark'>
                <span />
                <span />
              </span>
              <div className='page-title-text'>
                <h1 ref={this.myRef}>
                  <FaIcon iconName='fa fa-bug' /> Something went wrong...
                </h1>
              </div>
            </div>
          </div>
        </div>
        <div key='kerr2'>
          <div className='Fixedcontentwrapper'>
            <div className='m-3 p-3 border rounded bg-danger text-white lead'>
              <div>Error descripion:</div>
              <pre className='text-white'>{this.state.error}</pre>
              <pre className='text-white'>{this.state.info}</pre>
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    if (this.state.hasError) return this.renderError();
    if (this.props.titleAndLink.url) {
      window.history.pushState(
        { ...this.state.link.title },
        this.props.titleAndLink.title,
        urlHelper.getProperUrl() + this.props.titleAndLink.url
      );
    }

    const isUatEnv = process.env.REACT_APP_HOST_ENV === 'UAT';
    const isStageEnv = process.env.REACT_APP_HOST_ENV === 'staging' || isUatEnv;

    const rawTitle = this.props.titleAndLink.title ? this.props.titleAndLink.title : this.state.link.title;
    const section = this.props.titleAndLink.section;
    const showEyebrow = section && section !== rawTitle;
    const showIcon = this.props.titleAndLink.title && iconsEnabled();

    // When the current page has a user guide, the title itself opens it. clicks.url is empty on
    // the F5/deep-link path, so fall back to the browser path's slug (the same segment the load()
    // above matches menu entries by) — some detail pages are only identifiable by url.
    const guideUrl = this.props.titleAndLink.url || window.location.pathname.split('/')[1];

    return (
      <div className='Basecontent'>
        <div className='pageheader bravo-ds'>
          <div className='page-head'>
            <div className='page-title'>
              <span className='eq-mark'>
                <span />
                <span />
              </span>
              <div className='page-title-text'>
                {showEyebrow ? <div className='eyebrow'>{section}</div> : null}
                <h1 ref={this.myRef}>
                  {showIcon ? <FaIcon iconName={this.props.titleAndLink.icon} /> : null}
                  {showIcon ? JsxHelper.NonBreakableSpace() : null}
                  <UserGuideTitle
                    title={rawTitle}
                    url={guideUrl}
                  />
                </h1>
              </div>
              {isStageEnv ? (
                <span className={isUatEnv ? 'chip stage-chip uat-chip' : 'chip stage-chip'}>
                  {isUatEnv ? 'UAT' : 'STAGE'} · testing
                </span>
              ) : null}
            </div>
          </div>
        </div>
        {this.props.titleAndLink.link ? this.props.titleAndLink.link : this.state.link.link}
      </div>
    );
  }
}

const mapStateToProps = (store) => {
  const titleAndLink = getTitleContentLinkAndParameters(store);

  return { titleAndLink };
};

export default connect(mapStateToProps, { link_click })(Content);
